import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Smartphone, Download } from 'lucide-react';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Helmet } from 'react-helmet-async';

ChartJS.register(ArcElement, Tooltip, Legend);

const CostPerAppInstallCalculator = () => {
  const [totalCost, setTotalCost] = useState(10000);
  const [appInstalls, setAppInstalls] = useState(5000);
  const [costPerInstall, setCostPerInstall] = useState(0);

  useEffect(() => {
    calculateCostPerInstall();
  }, [totalCost, appInstalls]);

  const calculateCostPerInstall = () => {
    const calculatedCostPerInstall = appInstalls > 0 ? totalCost / appInstalls : 0;
    setCostPerInstall(parseFloat(calculatedCostPerInstall.toFixed(2)));
  };

  const chartData = {
    labels: ['Cost', 'Installs'],
    datasets: [
      {
        data: [totalCost, appInstalls * costPerInstall],
        backgroundColor: ['rgba(255, 99, 132, 0.6)', 'rgba(54, 162, 235, 0.6)'],
      },
    ],
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/cost-per-app-install-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Cost Per App Install Calculator: Free CPI Calculator Tool</title>
        <meta name="description" content="Calculate your Cost Per Install (CPI) instantly with our free calculator. Optimize your mobile app marketing campaigns and user acquisition costs effectively." />
        <meta name="keywords" content="CPI calculator, cost per install calculator, mobile app marketing, user acquisition metrics, app marketing ROI" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free CPI Calculator: Measure Your Cost Per App Install" />
        <meta property="og:description" content="Calculate your Cost Per Install instantly. Optimize your mobile app marketing campaigns with our free CPI calculator." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free CPI Calculator: Measure Your Cost Per App Install" />
        <meta property="twitter:description" content="Calculate your Cost Per Install instantly. Optimize your mobile app marketing campaigns with our free CPI calculator." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Last modified date */}
        <meta property="article:modified_time" content="2024-10-23" />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Cost Per App Install Calculator",
            "description": "Free calculator to measure Cost Per Install for mobile app marketing campaigns",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Cost Per Install",
              "Marketing cost tracking",
              "App install tracking",
              "CPI optimization tips",
              "Campaign performance analysis"
            ]
          })}
        </script>
      </Helmet>
      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Cost Per App Install Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Cost</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalCost}
                  onChange={(e) => setTotalCost(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="10000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Number of App Installs</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Smartphone className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={appInstalls}
                  onChange={(e) => setAppInstalls(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="5000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            ${costPerInstall}
            <span className="text-xl text-gray-500 ml-2">per install</span>
          </div>
          <p className="text-gray-600">Cost Per App Install</p>
          <div className="mt-6">
            <Pie data={chartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation</h2>
        <p className="text-gray-600">
          Your Cost Per App Install is ${costPerInstall}. This means you're spending an average of ${costPerInstall} to acquire each new app installation. A lower cost per install generally indicates a more efficient app marketing strategy, but it's important to consider this metric alongside others like user retention and in-app revenue to ensure overall profitability.
        </p>
        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Tips to Reduce Cost Per App Install</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Optimize your app store listing (ASO) to improve organic installs</li>
          <li>Use targeted advertising to reach your ideal user base</li>
          <li>Implement referral programs to encourage user-to-user promotion</li>
          <li>Leverage social media and influencer marketing</li>
          <li>Continuously test and optimize your ad creatives and messaging</li>
          <li>Focus on user retention to reduce the need for constant new installs</li>
        </ul>
      </div>
    </div>
  );
};

export default CostPerAppInstallCalculator;
